import { motion } from "framer-motion";

const NotificationOverloadSolution = () => {
  return (
    <div className="prose prose-invert prose-lg max-w-none">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold text-white mb-6 mt-8 first:mt-0"
      >
        The Hidden Cost of Notification Overload
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        In our hyperconnected world, notifications have become the silent productivity killer. Every ping, buzz, and pop-up is designed to grab your attention, but at what cost?
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        The Science Behind Distraction
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Research shows that it takes an average of <strong className="text-white font-semibold">23 minutes and 15 seconds</strong> to fully refocus after being interrupted by a notification. When you receive dozens of notifications daily, you're losing hours of productive time.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        The Attention Economy
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Tech companies have built entire business models around capturing and holding your attention. Each notification is carefully crafted to trigger dopamine responses, creating a cycle of:
      </motion.p>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Immediate gratification</strong> from checking messages
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Anxiety</strong> when notifications are delayed
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">FOMO</strong> (Fear of Missing Out) when you're not connected
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Reduced focus</strong> on important tasks
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        The Sera Solution
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Sera addresses this problem by implementing intelligent notification batching:
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        1. Smart Scheduling
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Instead of receiving notifications immediately, Sera groups them into scheduled batches. You choose when you want to review them.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        2. Priority Preservation
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Important notifications from family, work emergencies, or critical apps can still break through the batching system.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        3. Focus Restoration
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        By reducing constant interruptions, you can maintain deep focus for longer periods, leading to:
      </motion.p>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">Higher quality work output</li>
        <li className="text-slate-300">Reduced stress levels</li>
        <li className="text-slate-300">Better work-life balance</li>
        <li className="text-slate-300">Improved mental clarity</li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        Getting Started
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Ready to reclaim your focus? Here's how to begin:
      </motion.p>

      <motion.ol
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        className="list-decimal list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Download Sera</strong> from the Google Play Store
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Set your batch times</strong> - choose 2-3 times per day that work for you
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Configure priority apps</strong> - select which apps can break through batching
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Start batching</strong> - let Sera handle the rest
        </li>
      </motion.ol>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.9 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        The Results
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.0 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Users report:
      </motion.p>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.1 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">40% increase in focus time</li>
        <li className="text-slate-300">60% reduction in stress from notifications</li>
        <li className="text-slate-300">25% improvement in task completion rates</li>
      </motion.ul>

      <motion.blockquote
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.2 }}
        className="border-l-4 border-teal-400 pl-4 my-6 italic text-slate-300"
      >
        Ready to transform your relationship with notifications? Download Sera today and experience the difference.
      </motion.blockquote>
    </div>
  );
};

export default NotificationOverloadSolution;
